import * as ToastPrimitive from '@radix-ui/react-toast'
import { XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { createContext, useContext, useState, useCallback } from 'react'

export const ToastProvider = ToastPrimitive.Provider
export const ToastViewport = function ToastViewport({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Viewport>) {
  return (
    <ToastPrimitive.Viewport
      className={cn(
        'fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
        className
      )}
      {...props}
    />
  )
}

type ToastVariant = 'default' | 'success' | 'warning' | 'destructive' | 'error'

const variantClasses: Record<ToastVariant, string> = {
  default: 'border bg-background text-foreground',
  success: 'border-[color:var(--oc-emerald)] bg-background text-foreground',
  warning: 'border-[color:var(--oc-amber)] bg-background text-foreground',
  destructive: 'border-destructive bg-destructive/10 text-destructive',
  error: 'border-destructive bg-destructive/10 text-destructive',
}

export interface ToastProps extends React.ComponentProps<typeof ToastPrimitive.Root> {
  variant?: ToastVariant
}

export function Toast({ className, variant = 'default', ...props }: ToastProps) {
  return (
    <ToastPrimitive.Root
      className={cn(
        'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
        variantClasses[variant],
        className
      )}
      {...props}
    />
  )
}

export function ToastAction({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Action>) {
  return (
    <ToastPrimitive.Action
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

export function ToastClose({ className, ...props }: React.ComponentProps<typeof ToastPrimitive.Close>) {
  return (
    <ToastPrimitive.Close
      className={cn(
        'absolute right-1 top-1 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100',
        className
      )}
      aria-label="Dismiss notification"
      {...props}
    >
      <XIcon className="h-4 w-4" />
    </ToastPrimitive.Close>
  )
}

export function ToastTitle({ className, ...props }: React.ComponentProps<typeof ToastPrimitive.Title>) {
  return <ToastPrimitive.Title className={cn('text-sm font-semibold', className)} {...props} />
}

export function ToastDescription({ className, ...props }: React.ComponentProps<typeof ToastPrimitive.Description>) {
  return <ToastPrimitive.Description className={cn('text-sm opacity-90', className)} {...props} />
}

// ---------- useToast hook ----------

interface ToastEntry {
  id: string
  title?: string
  description?: string
  variant?: ToastVariant
  action?: React.ReactNode
  duration?: number
}

// Convenience shape used across the app
export interface AddToastOptions {
  type?: 'success' | 'error' | 'info' | 'warning'
  message?: string
  title?: string
  /** Optional secondary text (alias for message in ToastEntry). */
  description?: string
  /** Direct variant override — skips type-to-variant mapping. */
  variant?: ToastVariant
}

function toVariant(type?: AddToastOptions['type']): ToastVariant {
  if (type === 'error') return 'destructive'
  if (type === 'warning') return 'warning'
  if (type === 'success') return 'success'
  return 'default'
}

interface ToastContextValue {
  toasts: ToastEntry[]
  toast: (opts: Omit<ToastEntry, 'id'>) => void
  addToast: (opts: AddToastOptions) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastContextProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const toast = useCallback((opts: Omit<ToastEntry, 'id'>) => {
    const id = crypto.randomUUID()
    setToasts((prev) => [...prev, { ...opts, id }])
  }, [])

  const addToast = useCallback((opts: AddToastOptions) => {
    const id = crypto.randomUUID()
    setToasts((prev) => [
      ...prev,
      {
        id,
        variant: opts.variant ?? toVariant(opts.type),
        title: opts.title,
        description: opts.description ?? opts.message,
      },
    ])
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, toast, addToast, dismiss }}>
      <ToastProvider>
        {children}
        {toasts.map((t) => (
          <Toast key={t.id} variant={t.variant} duration={t.duration ?? 5000} onOpenChange={(open) => { if (!open) dismiss(t.id) }}>
            {t.title && <ToastTitle>{t.title}</ToastTitle>}
            {t.description && <ToastDescription>{t.description}</ToastDescription>}
            {t.action}
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside ToastContextProvider')
  return ctx
}
